import React from 'react';
import { useRouter } from 'next/router';
import { CardHcd } from '@maiia/model/generated/model/api-patient/api-patient';
import { NextSeo } from 'next-seo';
import InventoryPage from '@/components/templates/InventoryPage/InventoryPage';
import { hcdActions } from '@/src/actions';
import { useTranslation } from '@/src/i18n';
import InventorySection from '@/components/templates/InventoryPage/InventorySection';
import { APP_ROUTES } from '@/src/constants';
import { wrapper } from '@/src/store';
import waitSagaMiddleware from '@/src/ssr/waitSagaMiddleware';

type Props = {
  page: number;
  data: GetListApiResponse<CardHcd>;
};

const LIMIT_PER_PAGE = 90;

const PharmaciesIndex = ({ page, data }: Props) => {
  const router = useRouter();
  const { t } = useTranslation();
  const displayItems = data.items.map(p => ({
    card: p,
    url: p.url ?? APP_ROUTES.HOME,
  }));
  const showNext = page >= 1 && data.items.length === LIMIT_PER_PAGE;
  const showPrev = page > 1;
  const onNext = () => {
    router.push({
      pathname: router.pathname,
      query: {
        ...router.query,
        page: (page + 1).toString(),
      },
    });
  };
  const onPrev = () => {
    router.push({
      pathname: router.pathname,
      query: {
        ...router.query,
        page: (page - 1).toString(),
      },
    });
  };

  const breadcrumbs = [
    {
      name: 'Maiia',
      url: APP_ROUTES.HOME,
    },
    {
      name: t('directory_pharmacies'),
      url: APP_ROUTES.PHARMACIES.INDEX,
    },
  ];

  return (
    <>
      <NextSeo
        title={t('seo-title_directory_pharmacies')}
        description={t('seo-description_directory_pharmacies')}
      />
      <InventoryPage
        breadcrumbs={breadcrumbs}
        showNext={showNext}
        showPrev={showPrev}
        onNext={onNext}
        onPrev={onPrev}
      >
        <InventorySection title="directory_pharmacies" items={displayItems} />
      </InventoryPage>
    </>
  );
};

export const getServerSideProps = wrapper.getServerSideProps<Props>(
  store => async ({ query: { page: pageQs = '1' } }) => {
    const page = parseInt(pageQs as string, 10);
    if (page < 1) {
      return { props: { page, data: { items: [], total: 0 } } };
    }
    const data = await new Promise<GetListApiResponse<CardHcd>>(resolve => {
      const dispatchAction = hcdActions.getActions().getList({
        page: page - 1,
        'speciality.shortName': 'pharmacie',
        type: 'CENTER',
        // This route is made differently and expects a size parameter
        // As it might generate issues on the back side, we just change this
        // variable here even if it's not homogenous with the other calls
        size: LIMIT_PER_PAGE,
        // limit: LIMIT_PER_PAGE,
        sort: 'center._id',
        dir: 'desc',
      });
      dispatchAction.onSuccess = resolve;
      dispatchAction.onError = () => resolve({ items: [], total: 0 });
      store.dispatch(dispatchAction);
    });
    await waitSagaMiddleware(store);
    return { props: { page, data } };
  },
);

export default PharmaciesIndex;
